import React, { useCallback, useRef, useState } from 'react';

import ArrowUpwardIcon from '@mui/icons-material/ArrowUpward';
import MicIcon from '@mui/icons-material/Mic';
import StopIcon from '@mui/icons-material/Stop';
import { Box, Chip, CircularProgress, IconButton, InputBase, Stack, Tooltip, Typography, alpha } from '@mui/material';

import RecordingWaveform from '~/components/Common/RecordingWaveform';

import { transcribeAudio } from '~/services/AIChatService';

type VoiceState = 'idle' | 'recording' | 'transcribing';

const HAS_MEDIA_DEVICES = typeof navigator !== 'undefined' && !!navigator.mediaDevices?.getUserMedia;

interface AiChatInputProps {
  onSend: (message: string) => void;
  onStop?: () => void;
  isLoading: boolean;
  selectionOptions?: Array<string>;
  selectionLabel?: string | null;
}

export default function AiChatInput({
  onSend,
  onStop,
  isLoading,
  selectionOptions = [],
  selectionLabel = null,
}: AiChatInputProps) {
  const [input, setInput] = useState('');
  const [voiceState, setVoiceState] = useState<VoiceState>('idle');
  const [voiceError, setVoiceError] = useState<string | null>(null);
  const [recordingStream, setRecordingStream] = useState<MediaStream | null>(null);
  const hasSelectionOptions = selectionOptions.length > 0;

  const mediaRecorderRef = useRef<MediaRecorder | null>(null);
  const chunksRef = useRef<Array<Blob>>([]);
  const streamRef = useRef<MediaStream | null>(null);

  const handleSend = useCallback(() => {
    const trimmed = input.trim();
    if (!trimmed || isLoading) {
      return;
    }
    onSend(trimmed);
    setInput('');
  }, [input, isLoading, onSend]);

  const handleKeyDown = useCallback(
    (e: React.KeyboardEvent) => {
      if (e.key === 'Enter' && !e.shiftKey) {
        e.preventDefault();
        handleSend();
      }
    },
    [handleSend],
  );

  const handleSelectionClick = useCallback(
    (option: string) => {
      if (isLoading) {
        return;
      }
      onSend(`My department is ${option}.`);
    },
    [isLoading, onSend],
  );

  const stopStream = useCallback(() => {
    streamRef.current?.getTracks().forEach((t) => t.stop());
    streamRef.current = null;
    setRecordingStream(null);
  }, []);

  const handleTranscription = useCallback(async (blob: Blob) => {
    setVoiceState('transcribing');
    setVoiceError(null);
    try {
      const text = await transcribeAudio(blob);
      if (text) {
        setInput((prev) => (prev ? `${prev} ${text}` : text));
      }
    } catch {
      setVoiceError('Transcription failed. Please try again.');
    } finally {
      setVoiceState('idle');
    }
  }, []);

  const startRecording = useCallback(async () => {
    setVoiceError(null);
    try {
      const stream = await navigator.mediaDevices.getUserMedia({ audio: true });
      streamRef.current = stream;
      setRecordingStream(stream);
      chunksRef.current = [];

      const mimeType = MediaRecorder.isTypeSupported('audio/webm') ? 'audio/webm' : undefined;
      const recorder = new MediaRecorder(stream, mimeType ? { mimeType } : undefined);
      mediaRecorderRef.current = recorder;

      recorder.ondataavailable = (e) => {
        if (e.data.size > 0) {
          chunksRef.current.push(e.data);
        }
      };

      recorder.onstop = () => {
        const blob = new Blob(chunksRef.current, { type: recorder.mimeType });
        stopStream();
        void handleTranscription(blob);
      };

      recorder.start();
      setVoiceState('recording');
    } catch {
      stopStream();
      setVoiceError('Microphone access denied.');
      setVoiceState('idle');
    }
  }, [stopStream, handleTranscription]);

  const stopRecording = useCallback(() => {
    if (mediaRecorderRef.current?.state === 'recording') {
      mediaRecorderRef.current.stop();
    }
  }, []);

  const handleMicClick = useCallback(() => {
    if (voiceState === 'recording') {
      stopRecording();
    } else if (voiceState === 'idle') {
      startRecording();
    }
  }, [voiceState, startRecording, stopRecording]);

  const micDisabled = isLoading || voiceState === 'transcribing';
  const sendDisabled = !input.trim() || isLoading || hasSelectionOptions;
  const actionDisabled = isLoading ? !onStop : sendDisabled;
  const handleActionClick = isLoading && onStop ? onStop : handleSend;
  const isRecording = voiceState === 'recording';

  return (
    <Box sx={{ px: 2, pt: 1, pb: 1.25 }}>
      {hasSelectionOptions && (
        <>
          {selectionLabel && (
            <Typography
              variant="caption"
              color="text.secondary"
              sx={{
                mb: 0.75,
                display: 'block',
                fontSize: '0.7rem',
                letterSpacing: 0.3,
                textTransform: 'uppercase',
                fontWeight: 600,
              }}
            >
              {selectionLabel}
            </Typography>
          )}
          <Stack direction="row" spacing={1} sx={{ mb: 1.25, flexWrap: 'wrap', gap: 0.5 }}>
            {selectionOptions.map((option) => (
              <Chip
                key={option}
                label={option}
                size="small"
                color="primary"
                variant="outlined"
                onClick={() => handleSelectionClick(option)}
                disabled={isLoading}
                sx={{
                  maxWidth: '100%',
                  borderRadius: 999,
                  fontWeight: 500,
                  transition: 'transform 120ms ease, background-color 120ms ease',
                  '&:hover': { transform: 'translateY(-1px)' },
                }}
              />
            ))}
          </Stack>
        </>
      )}

      {voiceError && (
        <Typography variant="caption" color="error" sx={{ mb: 0.5, display: 'block' }}>
          {voiceError}
        </Typography>
      )}

      <Box
        sx={{
          display: 'flex',
          alignItems: 'flex-end',
          gap: 0.5,
          minHeight: 52,
          px: 1.5,
          py: 0.75,
          borderRadius: 3,
          bgcolor: (theme) =>
            theme.palette.mode === 'dark'
              ? alpha(theme.palette.common.white, 0.04)
              : alpha(theme.palette.text.primary, 0.025),
          border: 1,
          borderColor: (theme) => (isRecording ? theme.palette.primary.main : alpha(theme.palette.divider, 0.5)),
        }}
      >
        {isRecording && recordingStream ? (
          <Box sx={{ flex: 1, alignSelf: 'center' }}>
            <RecordingWaveform stream={recordingStream} />
          </Box>
        ) : (
          <InputBase
            fullWidth
            multiline
            maxRows={6}
            placeholder={hasSelectionOptions ? 'Choose a department above...' : 'Message the assistant'}
            value={input}
            onChange={(e) => setInput(e.target.value)}
            onKeyDown={handleKeyDown}
            disabled={hasSelectionOptions}
            sx={{
              flex: 1,
              fontSize: '0.9rem',
              py: 0.5,
              px: 0.75,
              lineHeight: 1.5,
              alignSelf: 'center',
              '& textarea': { resize: 'none' },
            }}
          />
        )}

        {HAS_MEDIA_DEVICES && (
          <Tooltip title={isRecording ? 'Stop recording' : 'Voice input'}>
            <span>
              <IconButton
                size="small"
                onClick={handleMicClick}
                disabled={micDisabled}
                sx={{
                  alignSelf: 'center',
                  color: isRecording ? 'error.main' : 'text.secondary',
                  bgcolor: isRecording ? (theme) => alpha(theme.palette.error.main, 0.1) : 'transparent',
                  width: 34,
                  height: 34,
                  transition: 'background-color 120ms ease, color 120ms ease',
                  '&:hover': {
                    bgcolor: isRecording
                      ? (theme) => alpha(theme.palette.error.main, 0.15)
                      : (theme) => alpha(theme.palette.text.primary, 0.06),
                    color: isRecording ? 'error.dark' : 'text.primary',
                  },
                }}
              >
                {voiceState === 'transcribing' ? (
                  <CircularProgress size={16} />
                ) : isRecording ? (
                  <StopIcon sx={{ fontSize: 17 }} />
                ) : (
                  <MicIcon sx={{ fontSize: 17 }} />
                )}
              </IconButton>
            </span>
          </Tooltip>
        )}

        <Tooltip title={isLoading ? 'Stop' : 'Send'}>
          <span>
            <IconButton
              onClick={handleActionClick}
              disabled={actionDisabled}
              size="small"
              aria-label={isLoading ? 'Stop' : 'Send'}
              sx={{
                alignSelf: 'center',
                width: 34,
                height: 34,
                background: (theme) =>
                  isLoading
                    ? theme.palette.error.main
                    : `linear-gradient(135deg, ${theme.palette.primary.main} 0%, ${theme.palette.primary.dark} 100%)`,
                color: 'primary.contrastText',
                boxShadow: (theme) =>
                  actionDisabled
                    ? 'none'
                    : `0 4px 10px -4px ${alpha(
                        isLoading ? theme.palette.error.main : theme.palette.primary.main,
                        0.5,
                      )}`,
                transition: 'transform 120ms ease, box-shadow 120ms ease, background-color 120ms ease',
                '&:hover': {
                  background: (theme) =>
                    isLoading
                      ? theme.palette.error.dark
                      : `linear-gradient(135deg, ${theme.palette.primary.light} 0%, ${theme.palette.primary.main} 100%)`,
                  transform: actionDisabled ? 'none' : 'translateY(-1px)',
                },
                '&.Mui-disabled': {
                  background: (theme) => alpha(theme.palette.text.primary, 0.08),
                  color: 'action.disabled',
                },
              }}
            >
              {isLoading ? <StopIcon sx={{ fontSize: 16 }} /> : <ArrowUpwardIcon sx={{ fontSize: 17 }} />}
            </IconButton>
          </span>
        </Tooltip>
      </Box>
    </Box>
  );
}
